import { API_BASE } from '@/services/api'

/**
 * Published desktop installers.
 *
 * <p>The download page asks the API what exists rather than linking a path it
 * hopes is there: a build is published by dropping files into the release
 * directory, so what is available changes without a redeploy of this app.
 */

export interface ReleaseBuild {
  platform: string
  filename: string
  sizeBytes: number
  sha256: string
  /** API-relative; pass through {@link downloadUrl} before using it. */
  downloadUrl: string
}

export interface ReleaseIndex {
  version: string
  generatedAt: string
  builds: ReleaseBuild[]
}

/** Nothing has been published yet — a normal state, not a failure. */
export const UNPUBLISHED = 'unpublished' as const

export async function fetchReleases(signal?: AbortSignal): Promise<ReleaseIndex | typeof UNPUBLISHED> {
  const response = await fetch(`${API_BASE}/api/v1/releases`, { signal })

  // The API answers 503 until a release is staged. That is the expected state
  // of a fresh environment, so it is reported rather than thrown.
  if (response.status === 503 || response.status === 404) {
    return UNPUBLISHED
  }
  if (!response.ok) {
    throw new Error(`Could not load the downloads (${response.status})`)
  }
  return (await response.json()) as ReleaseIndex
}

export function findBuild(index: ReleaseIndex, platform: string): ReleaseBuild | undefined {
  return index.builds.find((build) => build.platform === platform)
}

/** Absolute URL, so the desktop shell reaches the API on its own origin. */
export function downloadUrl(build: ReleaseBuild): string {
  return `${API_BASE}${build.downloadUrl}`
}

export function formatSize(bytes: number): string {
  if (!bytes || bytes < 0) return ''
  const mb = bytes / 1_000_000
  return mb >= 1000 ? `${(mb / 1000).toFixed(1)} GB` : `${mb.toFixed(0)} MB`
}
